import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isPasswordStrong, PASSWORD_REQUIREMENT_MESSAGE } from '@primetime/shared';
import { ApiError, api } from '../services/apiClient';

type ResetState = 'idle' | 'loading' | 'success' | 'error';
type TokenState = 'checking' | 'valid' | 'invalid';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetState, setResetState] = useState<ResetState>('idle');
  const [tokenState, setTokenState] = useState<TokenState>(token.length === 0 ? 'invalid' : 'checking');
  const [error, setError] = useState('');
  const missingToken = token.length === 0;
  const tokenInvalid = tokenState === 'invalid';

  useEffect(() => {
    let active = true;

    async function validateToken() {
      if (missingToken) {
        setTokenState('invalid');
        return;
      }

      setTokenState('checking');
      try {
        const result = await api<{ valid: boolean }>('/api/auth/password-reset/validate', {
          method: 'POST',
          body: JSON.stringify({ token })
        });
        if (active) setTokenState(result.valid ? 'valid' : 'invalid');
      } catch {
        if (active) setTokenState('invalid');
      }
    }

    validateToken();
    return () => {
      active = false;
    };
  }, [missingToken, token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    if (tokenInvalid) {
      setResetState('error');
      setError('Reset link is invalid or expired.');
      return;
    }

    if (password !== confirmPassword) {
      setResetState('error');
      setError('Passwords do not match.');
      return;
    }

    if (!isPasswordStrong(password)) {
      setResetState('error');
      setError(PASSWORD_REQUIREMENT_MESSAGE);
      return;
    }

    setResetState('loading');

    try {
      await api<{ ok: true }>('/api/auth/password-reset/complete', {
        method: 'POST',
        body: JSON.stringify({ token, password })
      });
      setPassword('');
      setConfirmPassword('');
      setResetState('success');
      navigate('/login', {
        replace: true,
        state: { message: 'Your password has been reset. You can sign in with your new password.' }
      });
    } catch (err) {
      setResetState('error');
      setError(err instanceof ApiError ? err.message : 'Unable to reset password. Please request a new link.');
    }
  }

  return (
    <section className="container-shell flex min-h-[75vh] items-center justify-center py-16">
      <form onSubmit={onSubmit} className="glass w-full max-w-md rounded-3xl p-8" autoComplete="off">
        <div className="text-center">
          <h1 className="text-3xl font-black">Create New Password</h1>
          <p className="mt-4 text-sm leading-6 text-white/60">
            {tokenState === 'checking'
              ? 'Checking your reset link.'
              : tokenInvalid
                ? 'Request a new password reset link to continue.'
                : 'Use a new password for your PrimeTime DJ Pool account.'}
          </p>
        </div>

        {tokenState === 'checking' && (
          <div className="mt-5 rounded-xl border border-white/15 bg-white/10 p-3 text-sm text-white/70">
            Checking reset link...
          </div>
        )}

        {tokenInvalid && (
          <div className="mt-5 rounded-xl border border-prime-red/40 bg-prime-red/10 p-3 text-sm text-red-200">
            Reset link is invalid or expired.
          </div>
        )}

        {resetState === 'success' && (
          <div className="mt-5 rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-3 text-sm text-emerald-100">
            Your password has been reset. You can sign in with your new password.
          </div>
        )}

        {resetState === 'error' && error && !tokenInvalid && (
          <div className="mt-5 rounded-xl border border-prime-red/40 bg-prime-red/10 p-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {tokenState === 'valid' && (
          <>
            <label className="mt-6 block text-sm font-bold text-white/70">New Password</label>
            <input
              className="form-input mt-2"
              type="password"
              value={password}
              autoComplete="new-password"
              required
              disabled={resetState === 'success'}
              onChange={(event) => setPassword(event.target.value)}
            />
            <p className="mt-2 text-xs leading-5 text-white/50">
              {PASSWORD_REQUIREMENT_MESSAGE}
            </p>

            <label className="mt-4 block text-sm font-bold text-white/70">Confirm Password</label>
            <input
              className="form-input mt-2"
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              required
              disabled={resetState === 'success'}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />

            <button
              className="btn-primary mt-6 w-full"
              type="submit"
              disabled={resetState === 'loading' || resetState === 'success'}
            >
              {resetState === 'loading' ? 'Resetting...' : 'Reset Password'}
            </button>
          </>
        )}

        <div className="mt-6 text-center text-sm text-white/55">
          <Link to={tokenInvalid ? '/forgot-password' : '/login'} className="text-prime-red hover:underline">
            {tokenInvalid ? 'Request a new reset link' : 'Back to sign in'}
          </Link>
        </div>
      </form>
    </section>
  );
}
